import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Loading, Empty, STAGE_STYLES, fmtDate } from "@/components/shared";
import LeadDrawer from "@/components/LeadDrawer";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Phone, Clock } from "lucide-react";
import { toast } from "sonner";

export default function FollowUps() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const filter = params.get("f") || "today";
  const openId = params.get("id");

  const { data: config } = useQuery({ queryKey: ["leadconfig"], queryFn: async () => (await api.get("/leads/meta/config")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["fq", filter], queryFn: async () => (await api.get("/leads/queues/followups", { params: { filter } })).data,
    refetchInterval: 30000,
  });
  const complete = useMutation({
    mutationFn: async (fid) => (await api.post(`/leads/followups/${fid}/complete`)).data,
    onSuccess: () => { toast.success("Follow-up completed"); qc.invalidateQueries(); },
  });

  const setFilter = (f) => setParams((p) => { p.set("f", f); return p; });
  const openLead = (id) => setParams((p) => { p.set("id", id); return p; });

  return (
    <div>
      <PageHeader title="Follow-ups" subtitle="Every active lead should have a next action" testid="followups-header" />
      <Tabs value={filter} onValueChange={setFilter} className="mb-4">
        <TabsList>
          <TabsTrigger value="today" data-testid="fq-today">Today</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="fq-overdue">Overdue</TabsTrigger>
          <TabsTrigger value="missing" data-testid="fq-missing">Missing Next Action</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? <Loading /> : !data?.items?.length ? (
        <Empty title="All clear!" subtitle="No items in this queue." />
      ) : data.kind === "followups" ? (
        <div className="space-y-2">
          {data.items.map((f) => (
            <div key={f.id} className="p-4 rounded-xl border bg-white dark:bg-slate-800 flex items-center justify-between gap-3" data-testid={`fq-item-${f.id}`}>
              <div className="cursor-pointer flex-1 min-w-0" onClick={() => f.lead && openLead(f.lead.id)}>
                <div className="font-semibold truncate">{f.lead?.name || "Lead"}</div>
                <div className="text-sm text-slate-500 flex items-center gap-2 flex-wrap">
                  <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{f.lead?.phone}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(f.due_at)}</span>
                  {f.type === "callback" && <span className="text-orange-600 text-xs font-semibold">callback</span>}
                </div>
                {f.note && <div className="text-xs text-slate-400 mt-0.5">{f.note}</div>}
              </div>
              <Button size="sm" variant="outline" className="gap-1.5 shrink-0" onClick={() => complete.mutate(f.id)} data-testid={`fq-done-${f.id}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Done
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {data.items.map((l) => (
            <div key={l.id} onClick={() => openLead(l.id)} className="p-4 rounded-xl border bg-white dark:bg-slate-800 flex items-center justify-between gap-3 cursor-pointer hover:shadow-md" data-testid={`fq-lead-${l.id}`}>
              <div>
                <div className="font-semibold">{l.name}</div>
                <div className="text-sm text-slate-500">{l.phone} • {l.course_interest || "—"}</div>
              </div>
              <span className={`text-[11px] font-semibold px-2 py-1 rounded-full border ${STAGE_STYLES[l.stage]}`}>{l.stage}</span>
            </div>
          ))}
        </div>
      )}

      {openId && <LeadDrawer leadId={openId} config={config} onClose={() => setParams((p) => { p.delete("id"); return p; })} onChanged={() => qc.invalidateQueries()} />}
    </div>
  );
}
